import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  History,
  ReceiptText,
  Send,
  Stethoscope,
  Upload,
  UserCheck,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { LiveSessionPanel } from '../components/LiveSessionPanel';
import {
  Button,
  Card,
  Field,
  Loading,
  Notice,
  PageHeader,
  StatusBadge,
  Tabs,
} from '../components/ui';
import { api, authenticatedBlobUrl, jsonBody } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ROLE_ACCESS } from '../lib/role-access';
import {
  hasAnyRole,
  type ExternalHandoffStatus,
  type RoleCode,
  type StudyDetail,
  type Transition,
} from '../lib/types';

const finalProcedureStatuses = ['REALIZADO', 'INCOMPLETO', 'NO_REALIZADO'];

const transitionCopy: Record<
  string,
  { label: string; title: string; description: string; requirement: string }
> = {
  COMPLETAR_INFO: {
    label: 'Confirmar datos del estudio',
    title: 'Confirmar datos iniciales',
    description:
      'Valida que el estudio ya tiene la información administrativa mínima para pasar a técnica.',
    requirement: 'Médico, fecha, motivo y contexto operativo según la ruta.',
  },
  INICIAR_ESTUDIO: {
    label: 'Iniciar captura técnica',
    title: 'Iniciar captura técnica',
    description:
      'Marca el estudio como en proceso y registra el inicio operativo de la atención técnica.',
    requirement: 'El estudio no debe estar marcado como no realizado.',
  },
  FINALIZAR_ESTUDIO: {
    label: 'Confirmar procedimiento finalizado',
    title: 'Confirmar procedimiento finalizado',
    description:
      'Cierra la etapa operativa de la técnica cuando la captura ya tiene un resultado final.',
    requirement:
      'La captura técnica debe quedar en REALIZADO, INCOMPLETO o NO REALIZADO.',
  },
  ENVIAR_REVISION: {
    label: 'Enviar a revisión médica',
    title: 'Enviar a revisión médica',
    description:
      'Entrega el estudio al médico responsable para lectura, informe y decisión clínica.',
    requirement:
      'Captura técnica completa y resultado final registrado; motivo si fue incompleto o no realizado.',
  },
  INICIAR_INFORME: {
    label: 'Iniciar informe',
    title: 'Iniciar informe médico',
    description:
      'Inicia la elaboración y abre directamente el espacio de redacción médica.',
    requirement: 'Ser el médico responsable asignado al estudio.',
  },
};

const transitionDestinations: Partial<Record<string, (studyId: number) => string>> = {
  INICIAR_ESTUDIO: (studyId) => `/studies/${studyId}/technical`,
  INICIAR_INFORME: (studyId) => `/studies/${studyId}/report`,
  ENVIAR_FACTURACION: () => '/work/billing',
};

function transitionLabel(item: Transition) {
  return transitionCopy[item.actionCode]?.label ?? item.actionLabel;
}

function transitionMeta(item: Transition) {
  return (
    transitionCopy[item.actionCode] ?? {
      label: item.actionLabel,
      title: item.actionLabel,
      description: 'Ejecuta la siguiente transición configurada para este estudio.',
      requirement: 'Debe cumplir las reglas del estado, rol, responsable y datos requeridos.',
    }
  );
}

function transitionBlockReason(study: StudyDetail, item: Transition) {
  if (
    item.actionCode === 'FINALIZAR_ESTUDIO' &&
    !finalProcedureStatuses.includes(study.procedureStatus)
  ) {
    return 'Primero guarde la captura técnica como realizado, incompleto o no realizado.';
  }

  if (item.actionCode === 'ENVIAR_REVISION') {
    if (!finalProcedureStatuses.includes(study.procedureStatus)) {
      return 'Primero registre el resultado final del procedimiento.';
    }
    if (
      study.procedureStatus !== 'REALIZADO' &&
      !study.nonPerformanceReason?.trim()
    ) {
      return 'Registre el motivo del resultado incompleto o no realizado.';
    }
    if (!Boolean(study.technicalCapture?.dataComplete)) {
      return 'Primero guarde una captura técnica completa.';
    }
  }

  return null;
}

function transitionVariant(actionCode: string) {
  if (actionCode === 'ANULAR') return 'danger';
  if (actionCode === 'DEVOLVER' || actionCode === 'DEVOLVER_TECNICA')
    return 'warning';
  return 'primary';
}

export function StudyDetailPage() {
  const id = Number(useParams().id);
  const navigate = useNavigate();
  const client = useQueryClient();
  const { user } = useAuth();
  const [message, setMessage] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [tab, setTab] = useState('summary');
  const query = useQuery({
    queryKey: ['study', id],
    queryFn: () => api<StudyDetail>(`/api/studies/${id}`),
  });
  const transitions = useQuery({
    queryKey: ['transitions', id, query.data?.stateCode, query.data?.rowVersion],
    queryFn: () => api<Transition[]>(`/api/studies/${id}/transitions`),
    enabled: Boolean(query.data),
  });

  const transition = useMutation({
    mutationFn: (actionCode: string) => {
      const s = query.data;
      if (!s) throw new Error('Estudio no disponible.');
      return api<Partial<StudyDetail>>(`/api/studies/${id}/transitions`, {
        method: 'POST',
        ...jsonBody({
          actionCode,
          comment: comment || null,
          expectedStateCode: s.stateCode,
          expectedRowVersion: s.rowVersion,
        }),
      });
    },
    onSuccess: async (updated, actionCode) => {
      const destination = transitionDestinations[actionCode]?.(id);
      setMessage('Acción completada correctamente.');
      setComment('');
      if (actionCode === 'ENVIAR_REVISION') {
        client.setQueryData<StudyDetail>(['study', id], (current) =>
          current ? { ...current, ...updated } : current,
        );
        client.setQueryData<Transition[]>(['transitions', id], []);
        await client.invalidateQueries({ queryKey: ['studies'] });
        return;
      }
      await Promise.all([
        client.invalidateQueries({ queryKey: ['study', id] }),
        client.invalidateQueries({ queryKey: ['transitions', id] }),
        client.invalidateQueries({ queryKey: ['studies'] }),
      ]);
      if (destination) navigate(destination);
    },
    onError: (e) =>
      setMessage(
        e instanceof Error ? e.message : 'No fue posible ejecutar la transición.',
      ),
  });

  const retryAutomation = useMutation({
    mutationFn: () => api(`/api/studies/${id}/retry-automation`, { method: 'POST' }),
    onSuccess: async () => {
      setMessage('Automatización reenviada correctamente.');
      await Promise.all([
        client.invalidateQueries({ queryKey: ['study', id] }),
        client.invalidateQueries({ queryKey: ['transitions', id] }),
        client.invalidateQueries({ queryKey: ['studies'] }),
      ]);
    },
    onError: (e) =>
      setMessage(
        e instanceof Error ? e.message : 'No fue posible reintentar la automatización.',
      ),
  });

  const refreshStudyOperations = async () => {
    await Promise.all([
      query.refetch(),
      client.invalidateQueries({ queryKey: ['transitions', id] }),
    ]);
  };

  if (query.isLoading) return <Loading />;
  if (query.error || !query.data)
    return <Notice kind="error">No fue posible cargar el estudio.</Notice>;

  const s = query.data;
  const links: Array<[string, string, typeof FileText, RoleCode[]]> = [
    ['Recepción', `/studies/${id}/reception`, UserCheck, ROLE_ACCESS.reception],
    [
      'Captura técnica',
      `/studies/${id}/technical`,
      Stethoscope,
      ROLE_ACCESS.technicalCapture,
    ],
    [
      'Informe médico',
      `/studies/${id}/report`,
      FileText,
      ['MEDICO', 'COORDINACION', 'ADMINISTRADOR', 'SUPERVISOR', 'AUDITOR', 'INVESTIGACION'],
    ],
    [
      'Documentos',
      `/studies/${id}/documents`,
      Upload,
      ['TECNICA', 'MEDICO', 'COORDINACION', 'ADMINISTRADOR', 'SUPERVISOR', 'AUDITOR', 'INVESTIGACION'],
    ],
    [
      'Historial',
      `/studies/${id}/history`,
      History,
      ['RECEPCION', 'TECNICA', 'MEDICO', 'FACTURACION', 'COORDINACION', 'ADMINISTRADOR', 'AUDITOR', 'SUPERVISOR', 'INVESTIGACION'],
    ],
  ];
  const canManageLive = hasAnyRole(user, [
    'TECNICA',
    'MEDICO',
    'COORDINACION',
    'ADMINISTRADOR',
  ]);
  const canManageDelivery = hasAnyRole(user, [
    'RECEPCION',
    'MEDICO',
    'COORDINACION',
    'ADMINISTRADOR',
    'SUPERVISOR',
  ]);
  const canReadBilling = hasAnyRole(user, [
    'FACTURACION',
    'COORDINACION',
    'ADMINISTRADOR',
    'SUPERVISOR',
    'AUDITOR',
  ]);
  const canRetryAutomation =
    s.stateCode === 'ERROR_AUT' &&
    hasAnyRole(user, ['ADMINISTRADOR', 'COORDINACION', 'SUPERVISOR']);
  const tabs = [
    { key: 'summary', label: 'Resumen' },
    ...(canManageLive
      ? [{ key: 'live', label: `Sesiones en vivo (${s.liveSessions.length})` }]
      : []),
    { key: 'external', label: 'SaludTools / sistemas externos' },
    ...(canManageDelivery ? [{ key: 'delivery', label: 'Entrega y correo' }] : []),
  ];

  return (
    <>
      <PageHeader
        title={`${s.code} · ${s.patientName}`}
        description={`${s.studyTypeName} · ${s.origin.replaceAll('_', ' ')} · ${new Date(s.studyDate).toLocaleString('es-CO')}`}
        actions={
          <>
            <StatusBadge value={s.stateCode} />
            <StatusBadge value={s.patient.dataStatus} />
          </>
        }
      />
      {message && (
        <Notice
          kind={
            message.includes('correctamente') ||
            message.includes('actualizad') ||
            message.includes('registrad') ||
            message.includes('cread') ||
            message.includes('enviado')
              ? 'success'
              : 'error'
          }
          onClose={() => setMessage(null)}
        >
          {message}
        </Notice>
      )}
      <div className="quick-actions">
        {links
          .filter(([, , , roles]) => hasAnyRole(user, roles))
          .map(([label, to, Icon]) => (
            <Link key={label} to={to}>
              <Icon size={18} />
              {label}
            </Link>
          ))}
      </div>
      <Tabs active={tab} onChange={setTab} items={tabs} />
      {tab === 'summary' && <Summary study={s} canReadBilling={canReadBilling} />}
      {tab === 'live' && canManageLive && (
        <LiveSessionPanel
          study={s}
          onMessage={setMessage}
          onRefresh={refreshStudyOperations}
        />
      )}
      {tab === 'external' && (
        <ExternalPanel
          study={s}
          onMessage={setMessage}
          onRefresh={refreshStudyOperations}
        />
      )}
      {tab === 'delivery' && canManageDelivery && (
        <DeliveryPanel
          study={s}
          onMessage={setMessage}
          onRefresh={refreshStudyOperations}
        />
      )}
      <ProcessActions
        study={s}
        transitions={transitions.data ?? []}
        comment={comment}
        setComment={setComment}
        canRetryAutomation={canRetryAutomation}
        transitionPending={transition.isPending}
        retryPending={retryAutomation.isPending}
        onTransition={(actionCode) => transition.mutate(actionCode)}
        onOpenDelivery={() => setTab('delivery')}
        onRetry={() => retryAutomation.mutate()}
        userRoles={user?.roles ?? []}
      />
    </>
  );
}

function ProcessActions({
  study,
  transitions,
  comment,
  setComment,
  canRetryAutomation,
  transitionPending,
  retryPending,
  onTransition,
  onOpenDelivery,
  onRetry,
  userRoles,
}: {
  study: StudyDetail;
  transitions: Transition[];
  comment: string;
  setComment: (value: string) => void;
  canRetryAutomation: boolean;
  transitionPending: boolean;
  retryPending: boolean;
  onTransition: (actionCode: string) => void;
  onOpenDelivery: () => void;
  onRetry: () => void;
  userRoles: RoleCode[];
}) {
  const manualTransitions = transitions.filter(
    (item) => !['ANULAR', 'ENVIAR'].includes(item.actionCode),
  );
  const annulTransitions = transitions.filter((item) => item.actionCode === 'ANULAR');

  return (
    <Card title="Acciones del proceso">
      <div className="process-summary">
        <div>
          <span>Proceso</span>
          <StatusBadge value={study.stateCode} />
        </div>
        <div>
          <span>Captura técnica</span>
          <StatusBadge value={study.procedureStatus} />
        </div>
      </div>
      <p className="muted">
        El proceso conserva la trazabilidad del estudio. La captura técnica define el
        resultado del procedimiento y habilita los siguientes pasos cuando corresponde.
      </p>

      {transitions.some((x) => x.requiresComment) && (
        <Field label="Comentario de transición">
          <textarea
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Obligatorio para devolución, anulación o corrección"
          />
        </Field>
      )}

      <div className="process-action-list">
        {study.stateCode === 'PEND_ENVIO' && (
          <div className="process-action">
            <div>
              <strong>Preparar y enviar informe</strong>
              <p>
                Revise el PDF y los destinatarios antes de realizar el envío.
              </p>
              <small>
                El estado avanzará únicamente cuando exista evidencia de correo exitoso.
              </small>
            </div>
            <Button onClick={onOpenDelivery}>Abrir entrega y correo</Button>
          </div>
        )}
        {manualTransitions.map((item) => {
          const meta = transitionMeta(item);
          const blockReason = transitionBlockReason(study, item);
          const disabled =
            Boolean(blockReason) || (item.requiresComment && !comment.trim());

          return (
            <div className="process-action" key={item.id}>
              <div>
                <strong>{meta.title}</strong>
                <p>{meta.description}</p>
                <small>Requisito: {meta.requirement}</small>
                {blockReason && <em>{blockReason}</em>}
              </div>
              <Button
                variant={transitionVariant(item.actionCode)}
                busy={transitionPending}
                disabled={disabled}
                onClick={() => onTransition(item.actionCode)}
              >
                {transitionLabel(item)}
              </Button>
            </div>
          );
        })}

        {canRetryAutomation && (
          <div className="process-action">
            <div>
              <strong>Reintentar automatización</strong>
              <p>Vuelve a ejecutar la automatización fallida del estudio.</p>
              <small>Disponible para recuperación operativa de errores automatizados.</small>
            </div>
            <Button busy={retryPending} onClick={onRetry}>
              Reintentar automatización
            </Button>
          </div>
        )}

        {annulTransitions.map((item) => (
          <div className="process-action process-action-danger" key={item.id}>
            <div>
              <strong>Anular estudio</strong>
              <p>Detiene el flujo operativo y exige justificación en historial.</p>
              <small>Use esta acción solo cuando corresponda cerrar el caso por anulación.</small>
            </div>
            <Button
              variant="danger"
              busy={transitionPending}
              disabled={item.requiresComment && !comment.trim()}
              onClick={() => onTransition(item.actionCode)}
            >
              {item.actionLabel}
            </Button>
          </div>
        ))}
      </div>

      {!transitions.length && !canRetryAutomation && (
        <span className="muted">
          No hay transiciones manuales disponibles para {userRoles.join(', ')}.
        </span>
      )}
    </Card>
  );
}

function Summary({
  study: s,
  canReadBilling,
}: {
  study: StudyDetail;
  canReadBilling: boolean;
}) {
  return (
    <div className="two-column">
      <Card title="Datos del estudio">
        <dl className="detail-list">
          <dt>Paciente</dt>
          <dd>
            <Link to={`/patients/${s.patientId}`}>{s.patientName}</Link>
            <small>
              {s.patientCode} · {s.normalizedDocument}
            </small>
          </dd>
          <dt>Ruta</dt>
          <dd>{s.origin.replaceAll('_', ' ')}</dd>
          <dt>Institución / sede</dt>
          <dd>
            {s.institutionName ||
              (s.origin === 'CLINICA_EXTERNA'
                ? 'Ubicación externa no catalogada'
                : 'Consultorio')}
            {s.serviceLocation && <small>{s.serviceLocation}</small>}
          </dd>
          <dt>Motivo</dt>
          <dd>{s.reason}</dd>
          <dt>Remitente</dt>
          <dd>
            {s.referringProfessional || 'No registrado'}
            <small>{s.referringInstitution}</small>
          </dd>
          <dt>Profusion</dt>
          <dd>
            {s.profusionPatientCode || 'Sin código de paciente'}
            <small>{s.profusionStudyCode || 'Sin código de estudio'}</small>
          </dd>
        </dl>
      </Card>
      <Card title="Control operativo">
        <dl className="detail-list">
          <dt>Responsable</dt>
          <dd>
            {s.currentResponsibleRole.replaceAll('_', ' ')}
            <small>{s.currentResponsibleEmail}</small>
          </dd>
          <dt>Procedimiento</dt>
          <dd>
            <StatusBadge value={s.procedureStatus} />
          </dd>
          <dt>Generación</dt>
          <dd>
            <StatusBadge value={s.generationStatus} />
          </dd>
          <dt>Entrega</dt>
          <dd>
            <StatusBadge value={s.deliveryStatus} />
          </dd>
          {canReadBilling && (
            <>
              <dt>Facturación</dt>
              <dd>
                <StatusBadge value={s.billingHandoff?.status || 'NO_REQUERIDA'} />
              </dd>
            </>
          )}
          <dt>Documentos</dt>
          <dd>{s.documents.length}</dd>
          <dt>Sesión en vivo</dt>
          <dd>{s.requiresLiveSupport ? 'Requerida / registrada' : 'No requerida'}</dd>
        </dl>
      </Card>
      <Card title="Datos administrativos">
        <dl className="detail-list">
          <dt>Condición</dt>
          <dd>{s.administrativeCondition || 'Sin registrar'}</dd>
          <dt>Convenio</dt>
          <dd>
            {s.agreementName || 'Sin convenio'}
            {s.thirdPartyName && (
              <small>
                {s.thirdPartyName} · ID externo {s.agreementExternalId}
              </small>
            )}
          </dd>
          <dt>Asegurador / EAPB</dt>
          <dd>{s.payerName || 'Sin registrar'}</dd>
          <dt>Autorización</dt>
          <dd>{s.authorizationNumber || 'Sin registrar'}</dd>
          <dt>Observación</dt>
          <dd>{s.administrativeObservation || 'Sin observación'}</dd>
        </dl>
      </Card>
      <Card title="Últimos eventos">
        <div className="timeline mini">
          {s.history.slice(0, 6).map((event) => (
            <div key={event.id}>
              <span />
              <div>
                <strong>{event.actionCode}</strong>
                <p>
                  {event.previousStateCode || 'INICIO'} → {event.newStateCode}
                </p>
                <small>
                  {event.executedByEmail} ·{' '}
                  {new Date(event.transitionedAt).toLocaleString('es-CO')}
                </small>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function ExternalPanel({
  study,
  onMessage,
  onRefresh,
}: {
  study: StudyDetail;
  onMessage: (m: string) => void;
  onRefresh: () => Promise<unknown>;
}) {
  const existing = study.externalHandoffs.find(
    (x) => x.systemType === 'SALUDTOOLS',
  );
  const [status, setStatus] = useState<ExternalHandoffStatus>(
    existing?.status ?? 'PENDIENTE',
  );
  const [reference, setReference] = useState(existing?.externalReference ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const mutation = useMutation({
    mutationFn: () =>
      api(`/api/studies/${study.id}/external-handoffs`, {
        method: 'PUT',
        ...jsonBody({
          systemType: 'SALUDTOOLS',
          status,
          externalReference: reference || null,
          notes: notes || null,
          metadata: { manual: true },
        }),
      }),
    onSuccess: async () => {
      onMessage('Handoff a SaludTools actualizado.');
      await onRefresh();
    },
    onError: (e) =>
      onMessage(
        e instanceof Error ? e.message : 'No fue posible actualizar el handoff.',
      ),
  });
  return (
    <Card title="Incorporación manual a SaludTools">
      <Notice kind="info">
        SaludTools continúa como historia clínica oficial. Esta acción registra la
        entrega o verificación manual del informe definitivo.
      </Notice>
      <div className="form-grid three">
        <Field label="Estado">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as ExternalHandoffStatus)}
          >
            {['PENDIENTE', 'REGISTRADO', 'VERIFICADO', 'NO_APLICA', 'ERROR'].map(
              (x) => (
                <option key={x}>{x}</option>
              ),
            )}
          </select>
        </Field>
        <Field label="Referencia externa">
          <input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="ID, folio o referencia en SaludTools"
          />
        </Field>
        <Field label="Notas">
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
      <Button busy={mutation.isPending} onClick={() => mutation.mutate()}>
        Guardar handoff
      </Button>
    </Card>
  );
}

export function DeliveryPanel({
  study,
  onMessage,
  onRefresh,
}: {
  study: StudyDetail;
  onMessage: (m: string) => void;
  onRefresh: () => Promise<unknown>;
}) {
  const [sendToPatient, setSendToPatient] = useState(study.sendToPatient);
  const [sendToAilyn, setSendToAilyn] = useState(study.sendToAilyn);
  const [recipients, setRecipients] = useState(study.additionalRecipients.join(', '));
  const [documentId, setDocumentId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState('');

  useEffect(() => {
    const doc = study.documents.find(
      (x) => x.type === 'INFORME_FINAL' && ['GENERADO', 'ENVIADO'].includes(x.status),
    );
    if (doc) setDocumentId(String(doc.id));
  }, [study.documents]);

  const settings = useMutation({
    mutationFn: () =>
      api(`/api/studies/${study.id}/delivery-settings`, {
        method: 'PUT',
        ...jsonBody({
          sendToPatient,
          sendToAilyn,
          additionalRecipients: recipients
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean),
          rowVersion: study.rowVersion,
        }),
      }),
    onSuccess: async () => {
      onMessage('Destinatarios actualizados.');
      await onRefresh();
    },
    onError: (e) =>
      onMessage(
        e instanceof Error ? e.message : 'No fue posible guardar destinatarios.',
      ),
  });
  const send = useMutation({
    mutationFn: () =>
      api(`/api/studies/${study.id}/send`, {
        method: 'POST',
        ...jsonBody({
          documentId: Number(documentId),
          sendToPatient,
          sendToAilyn,
          additionalRecipients: recipients
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean),
          subjectOverride: subject || null,
          bodyOverride: body || null,
        }),
      }),
    onSuccess: async () => {
      onMessage('Informe enviado por correo correctamente.');
      await onRefresh();
    },
    onError: (e) =>
      onMessage(e instanceof Error ? e.message : 'No fue posible enviar el informe.'),
  });

  const selectedDocument = study.documents.find((document) => String(document.id) === documentId);
  const canPreviewSelectedDocument = selectedDocument?.mimeType === 'application/pdf';
  const deliverySettingsLocked = [
    'GENERANDO_DOC',
    'DOC_GENERADO',
    'PEND_ENVIO',
    'ENVIADO',
    'PEND_FACTURACION',
    'FACTURADO',
    'CERRADO',
    'ANULADO',
  ].includes(study.stateCode);
  const additionalRecipients = recipients
    .split(',')
    .map((recipient) => recipient.trim())
    .filter(Boolean);
  const hasDeliveryRecipient =
    sendToPatient || sendToAilyn || additionalRecipients.length > 0;

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    setPreviewUrl(null);
    setPreviewError('');

    if (!documentId || !canPreviewSelectedDocument) return undefined;

    authenticatedBlobUrl(`/api/documents/${documentId}/download`)
      .then(({ url }) => {
        objectUrl = url;
        if (active) setPreviewUrl(url);
        else URL.revokeObjectURL(url);
      })
      .catch(() => {
        if (active) setPreviewError('No fue posible cargar la previsualización del PDF.');
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [documentId, canPreviewSelectedDocument]);

  return (
    <div className="two-column">
      <Card title={deliverySettingsLocked ? 'Destinatarios de este envío' : 'Destinatarios previstos'}>
        <div className="checkbox-grid">
          <label>
            <input
              type="checkbox"
              checked={sendToPatient}
              onChange={(e) => setSendToPatient(e.target.checked)}
            />{' '}
            Paciente · {study.patient.email || 'sin correo'}
          </label>
          <label>
            <input
              type="checkbox"
              checked={sendToAilyn}
              onChange={(e) => setSendToAilyn(e.target.checked)}
            />{' '}
            Facturación / Ailyn
          </label>
        </div>
        <Field label="Destinatarios adicionales">
          <input
            value={recipients}
            onChange={(e) => setRecipients(e.target.value)}
            placeholder="correo1@dominio.com, correo2@dominio.com"
          />
        </Field>
        {deliverySettingsLocked ? (
          <Notice kind="info">
            Esta selección se aplicará únicamente al envío actual. Puede excluir al
            paciente y enviar a facturación, a destinatarios adicionales o a ambos.
          </Notice>
        ) : (
          <Button busy={settings.isPending} onClick={() => settings.mutate()}>
            Guardar destinatarios previstos
          </Button>
        )}
      </Card>
      <Card title="Enviar informe">
        <Field label="PDF final">
          <select value={documentId} onChange={(e) => setDocumentId(e.target.value)}>
            <option value="">Seleccione…</option>
            {study.documents
              .filter((x) => x.type === 'INFORME_FINAL')
              .map((x) => (
                <option key={x.id} value={x.id}>
                  {x.fileName} · {x.status}
                </option>
              ))}
          </select>
        </Field>
        <Field label="Asunto personalizado">
          <input value={subject} onChange={(e) => setSubject(e.target.value)} />
        </Field>
        <Field label="Cuerpo personalizado">
          <textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} />
        </Field>
        <p className="muted">
          Esta acción envía el PDF seleccionado por correo a los destinatarios indicados.
        </p>
        <Button
          disabled={
            study.stateCode !== 'PEND_ENVIO' ||
            !documentId ||
            !hasDeliveryRecipient
          }
          busy={send.isPending}
          onClick={() => send.mutate()}
        >
          <Send size={16} /> Enviar informe
        </Button>
        {study.stateCode !== 'PEND_ENVIO' && (
          <p className="muted">El envío se habilita al llegar al estado PEND_ENVIO.</p>
        )}
        {study.stateCode === 'PEND_ENVIO' && !hasDeliveryRecipient && (
          <p className="muted">Seleccione al menos un destinatario para realizar el envío.</p>
        )}
      </Card>
      <Card title="Entregas registradas">
        <div className="compact-list">
          {study.emailDeliveries.length ? (
            study.emailDeliveries.map((delivery: any, index) => (
              <div className="compact-row vertical" key={String(delivery.id ?? index)}>
                <strong>
                  {String(delivery.status)} · {String(delivery.provider)} ·{' '}
                  {String(delivery.sender)}
                </strong>
                <small>
                  {Array.isArray(delivery.recipients)
                    ? delivery.recipients.join(', ')
                    : ''}
                </small>
              </div>
            ))
          ) : (
            <p className="muted">No hay entregas registradas.</p>
          )}
        </div>
      </Card>
      <Card title="Facturación">
        <StatusBadge value={study.billingHandoff?.status || 'NO_REQUERIDA'} />
        {study.billingHandoff && (
          <dl className="detail-list">
            <dt>Receptor</dt>
            <dd>{study.billingHandoff.recipientEmail || 'Sin configurar'}</dd>
            <dt>Factura</dt>
            <dd>{study.billingHandoff.invoiceNumber || 'Pendiente'}</dd>
          </dl>
        )}
        <Link className="button button-secondary" to="/work/billing">
          <ReceiptText size={16} /> Abrir bandeja
        </Link>
      </Card>
      <div className="delivery-preview-card">
        <Card title="Previsualización del informe">
          {!selectedDocument ? (
            <p className="muted">Seleccione un PDF final para previsualizarlo.</p>
          ) : !canPreviewSelectedDocument ? (
            <p className="muted">El documento seleccionado no es un PDF previsualizable.</p>
          ) : previewError ? (
            <Notice kind="error">{previewError}</Notice>
          ) : previewUrl ? (
            <iframe
              className="pdf-preview-frame"
              src={previewUrl}
              title={`Previsualización de ${selectedDocument.fileName}`}
            />
          ) : (
            <Loading label="Cargando previsualización…" />
          )}
        </Card>
      </div>
    </div>
  );
}
